import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { _verify_sms_magic_link } from "../common/axios_services";
import { fetchData, profileData, setUserData } from "../slices/profileSlice";
import axiosCall from "../common/axios_call";
import { Spin, Alert, Button, Input, Form, message, Card } from "antd";
import {
  LoadingOutlined,
  CheckCircleFilled,
  UserOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";

const VerifySmsLinkPage = () => {
  const [searchParams] = useSearchParams();
  const { token: routeToken } = useParams();
  const token = searchParams.get("token") || routeToken;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [verifiedData, setVerifiedData] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [form] = Form.useForm();

  const antIcon = <LoadingOutlined style={{ fontSize: 36 }} spin />;

  useEffect(() => {
    if (!token) {
      setError("No verification token provided in URL.");
      setLoading(false);
      return;
    }

    const verify = async () => {
      try {
        setLoading(true);
        const res = await _verify_sms_magic_link(token);
        const data = res?.data;

        if (data?.tokens?.accessToken) {
          localStorage.setItem("konnect_token", data.tokens.accessToken);
          localStorage.setItem("token", data.tokens.accessToken);
          if (data.tokens.refreshToken) {
            localStorage.setItem("refreshToken", data.tokens.refreshToken);
          }
        }

        setVerifiedData(data);

        if (data?.user) {
          dispatch(setUserData(data.user));
          dispatch(profileData(data.user));
          dispatch(fetchData(true));
          form.setFieldsValue({
            firstName: data.user.firstName || "",
            lastName: data.user.lastName || "",
            email: data.user.email || "",
            phoneNumber: data.user.phoneNumber || "",
            state: data.user.state || "",
            lga: data.user.lga || "",
          });
        }

        const bonusText =
          Number(data?.bonusAmount) > 0
            ? `₦${Number(data.bonusAmount).toLocaleString("en-US")}`
            : "₦5,000";
        message.success(
          data?.wasAlreadyVerified
            ? (data?.message || "Your phone is already verified!")
            : `Congratulations! Your ${bonusText} bonus is ready.`
        );
      } catch (err) {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Verification link is invalid or expired."
        );
      } finally {
        setLoading(false);
      }
    };

    verify();
  }, [token, form, dispatch, navigate]);

  const handleUpdateProfile = async (values) => {
    try {
      setUpdating(true);
      const jwtToken = localStorage.getItem("konnect_token");
      const res = await axiosCall.patch("/users/me", values, {
        headers: { Authorization: `Bearer ${jwtToken}` },
      });
      const updatedUser = res?.data?.data || res?.data;
      if (updatedUser) {
        dispatch(setUserData(updatedUser));
        dispatch(profileData(updatedUser));
        dispatch(fetchData(true));
      }
      message.success("Profile updated successfully!");
      setTimeout(() => {
        navigate("/home");
      }, 1000);
    } catch (err) {
      message.error(
        err?.response?.data?.message || err?.message || "Failed to update profile"
      );
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4 flex flex-col items-center justify-center">
      <div className="w-full max-w-xl">
        {loading ? (
          <Card className="text-center py-12 shadow-sm rounded-xl">
            <Spin indicator={antIcon} />
            <h3 className="mt-6 font-bold text-gray-800 text-lg">
              Verifying Phone Number...
            </h3>
            <p className="text-gray-500 text-sm mt-1">
              Please wait while we verify your account.
            </p>
          </Card>
        ) : error ? (
          <Card className="shadow-sm rounded-xl text-center py-8">
            <Alert
              message="Verification Failed"
              description={error}
              type="error"
              showIcon
              className="mb-6"
            />
            <Button type="primary" size="large" onClick={() => navigate("/home")}>
              Home
            </Button>
          </Card>
        ) : (
          <div className="space-y-6">
            <Card className="shadow-sm rounded-xl border-green-200 bg-green-50/60">
              <div className="flex items-start gap-4">
                <CheckCircleFilled className="text-green-600 text-3xl mt-1 shrink-0" />
                <div className="flex-1">
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                    <h2 className="text-xl font-bold text-green-900 mb-0">
                      Congratulations! Your ₦{Number(verifiedData?.bonusAmount || 5000).toLocaleString("en-US")} Konnect Bonus is waiting for you.
                    </h2>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-200 text-green-900 ring-1 ring-green-300">
                      {verifiedData?.wasAlreadyVerified
                        ? "Bonus Active in Wallet"
                        : `+₦${Number(verifiedData?.bonusAmount || 5000).toLocaleString("en-US", { minimumFractionDigits: 2 })} Bonus`}
                    </span>
                  </div>
                  <p className="text-green-800 text-sm leading-relaxed mb-4">
                    Use it to buy food, electronics, and lots more.
                  </p>

                  <div className="rounded-xl border border-green-200/80 bg-white/80 p-4 text-left space-y-2.5 mb-4">
                    <div className="flex items-center gap-3 text-green-950 font-medium text-sm">
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-600 text-white text-xs font-bold">
                        ✓
                      </span>
                      <span>Download the Konnect app</span>
                    </div>
                    <div className="flex items-center gap-3 text-green-950 font-medium text-sm">
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-600 text-white text-xs font-bold">
                        ✓
                      </span>
                      <span>Open your wallet</span>
                    </div>
                    <div className="flex items-center gap-3 text-green-950 font-medium text-sm">
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-600 text-white text-xs font-bold">
                        ✓
                      </span>
                      <span>Enjoy your ₦{Number(verifiedData?.bonusAmount || 5000).toLocaleString("en-US")} bonus</span>
                    </div>
                  </div>

                  <Button
                    type="primary"
                    size="large"
                    onClick={() => navigate("/home")}
                    className="bg-green-600 hover:bg-green-700 border-none font-gilroyBold flex items-center gap-2"
                  >
                    <span>Visit Home Page</span>
                    <ArrowRightOutlined />
                  </Button>
                </div>
              </div>
            </Card>

            <Card
              title={
                <div className="flex items-center gap-2">
                  <UserOutlined className="text-blue-600" />
                  <span className="font-bold text-base">Your Profile Details</span>
                </div>
              }
              className="shadow-sm rounded-xl"
            >
              <Form form={form} layout="vertical" onFinish={handleUpdateProfile}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Form.Item
                    name="firstName"
                    label="First Name"
                    rules={[{ required: true, message: "Required" }]}
                  >
                    <Input size="large" placeholder="First Name" />
                  </Form.Item>

                  <Form.Item
                    name="lastName"
                    label="Last Name"
                    rules={[{ required: true, message: "Required" }]}
                  >
                    <Input size="large" placeholder="Last Name" />
                  </Form.Item>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Form.Item name="email" label="Email Address">
                    <Input size="large" disabled placeholder="Email" />
                  </Form.Item>

                  <Form.Item name="phoneNumber" label="Phone Number">
                    <Input size="large" disabled placeholder="Phone Number" />
                  </Form.Item>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Form.Item name="state" label="State">
                    <Input size="large" placeholder="State" />
                  </Form.Item>

                  <Form.Item name="lga" label="LGA">
                    <Input size="large" placeholder="LGA" />
                  </Form.Item>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Button
                    type="primary"
                    size="large"
                    htmlType="submit"
                    loading={updating}
                    block
                  >
                    Save Profile Changes
                  </Button>
                  <Button
                    size="large"
                    onClick={() => { window.location.href = "/home"; }}
                    block
                  >
                    Go to Home Page
                  </Button>
                </div>
              </Form>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifySmsLinkPage;
